import OpenAI from "openai";
import { fetchGameImages } from "./igdb";
import { batchFindLibraryMatches } from "./libraryMatcher";

// eslint-disable-next-line no-unused-vars
const EMULATABLE_PLATFORMS = [
  'PS', 'PS1', 'PSP', 'Atari', 'NDS', 'FC', 'NES', 
  'MD', 'Genesis', 'Flash', 'Infocom', 'Arcade', 
  'Apple II', 'GBA', 'DOS', 'SFC', 'SNES', 'Virtual Boy', 'J2ME'
];

const client = new OpenAI({
  apiKey: process.env.REACT_APP_LLM_API_KEY,
  baseURL: process.env.REACT_APP_LLM_BASE_URL || "https://dashscope.aliyuncs.com/compatible-mode/v1",
  dangerouslyAllowBrowser: true,
});

const QWEN_MODEL = "qwen3.7-flash";

/**
 * Helper: parse TSV response from LLM into array of game objects.
 * Expected columns: title_cn, title_en, platform, developer, publisher, release_date, description, isEmulatable
 */
function parseTsv(text) {
  if (!text) return [];

  // Strip markdown code fences if present
  let cleaned = text.trim();
  const fenceMatch = cleaned.match(/```[^\n]*\n([\s\S]*?)```/);
  if (fenceMatch) {
    cleaned = fenceMatch[1].trim();
  }

  const lines = cleaned.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length < 2) return []; // need header + at least one data row

  const EXPECTED_HEADERS = ['title_cn', 'title_en', 'platform', 'developer', 'publisher', 'release_date', 'description', 'isEmulatable'];

  // Check if first line is a header row
  const firstLineLower = lines[0].toLowerCase();
  let dataStart = 0;
  if (EXPECTED_HEADERS.some(h => firstLineLower.includes(h))) {
    dataStart = 1; // skip header
  }

  const results = [];
  for (let i = dataStart; i < lines.length; i++) {
    const cols = lines[i].split('\t');
    if (cols.length < 8) continue; // skip malformed rows

    results.push({
      title_cn: cols[0]?.trim() || '',
      title_en: cols[1]?.trim() || '',
      platform: cols[2]?.trim() || '',
      developer: cols[3]?.trim() || '',
      publisher: cols[4]?.trim() || '',
      release_date: cols[5]?.trim() || '',
      description: cols[6]?.trim() || '',
      isEmulatable: (['yes', 'maybe', 'no'].includes(cols[7]?.trim().toLowerCase()) ? cols[7].trim().toLowerCase() : 'no'),
    });
  }

  return results;
}

/**
 * Search for game candidates by name
 */
export async function searchGamesByName(gameName, platform, onProgress) {
  try {
    // Report LLM stage start
    onProgress?.('llm', 0);
    
    const platformHint = platform ? ` on platform: "${platform}"` : '';
    
    const stream = await client.chat.completions.create({
      model: QWEN_MODEL,
      stream: true,
      messages: [
        {
          role: "system",
          content: `Role: Expert Video Game Database Specialist.
You must return results as TSV (tab-separated values) with exactly these columns in order:
title_cn\ttitle_en\tplatform\tdeveloper\tpublisher\trelease_date\tdescription\tisEmulatable

Include a header row first, then one row per game. Use a single tab character between columns. Do NOT use any other format. Do NOT wrap in code fences.

Instructions:
1. Find ALL versions/releases of the requested game across different platforms
2. Each platform version is a SEPARATE game entry (e.g., "Super Mario Bros." on NES and "Super Mario Bros." on Game Boy Advance are TWO different games)
3. If the name is in Chinese, find the Official English/Romanized Title
4. Return up to 4 entries, but return fewer if fewer exists. Do NOT pad or duplicate results.  
5. Include detailed game description in Chinese. The description must NOT contain tab or newline characters.
6. Determine if the game is emulatable:
   - Set "yes" only when the platform is exactly one of these platforms (or the explicit alias in parentheses): PS / PS1 / original PlayStation, PSP, Atari 2600, NDS, FC, NES, MD / Genesis, Flash, Infocom, Arcade, Apple II, GBA, DOS, SFC / SNES, Virtual Boy, J2ME.
   - Platform matching must be exact, not prefix, substring, family, or fuzzy matching. In particular, PS2, PS3, PS4, and PS5 are NOT PS/PS1 and must be "no". Other Atari systems are NOT Atari 2600.
   - For Windows 95/98 games (Win95, Win98, Windows 95, Windows 98) → set isEmulatable to "maybe" (these may be emulatable but not guaranteed)
   - For later Windows games (Windows XP, Vista, 7, 8, 10, 11, or just "Windows"/"PC") → set isEmulatable to "no"
   - For all other platforms → set isEmulatable to "no"

TITLE RULE: Do not put version, edition, platform, region, year, remake/remaster, or other qualifiers in title_cn or title_en. Keep only the proper game title.

CRITICAL: Each result must have exactly ONE platform. If a game exists on multiple platforms, return multiple separate entries.
IMPORTANT: Return ALL platforms where this game was released, not just emulatable ones.`
        },
        {
          role: "user",
          content: `Search and retrieve comprehensive metadata for the game: "${gameName}"${platformHint}`
        }
      ],
    });

    // Collect streamed response
    let fullContent = "";
    for await (const chunk of stream) {
      const delta = chunk.choices?.[0]?.delta?.content;
      if (delta) {
        fullContent += delta;
      }
    }

    console.log('[searchGamesByName] === RAW LLM RESPONSE ===');
    console.log(fullContent);
    console.log('[searchGamesByName] === END RAW LLM RESPONSE ===');

    const results = parseTsv(fullContent);
    console.log('[searchGamesByName] Parsed results:', JSON.stringify(results, null, 2));
    
    // Report images stage start
    onProgress?.('images', 0);
    
    // Fetch images from IGDB API for each result
    let completedImages = 0;
    const totalImages = results.length || 1;
    
    const enrichedResults = await Promise.all(
      results.map(async (item) => {
        const gameTitle = item.title_en || item.title_cn;
        const platformName = item.platform;
        
        // Fetch images from IGDB
        let poster = null;
        let screenshots = [];
        let igdbFound = false;
        
        try {
          const images = await fetchGameImages(gameTitle, platformName);
          igdbFound = images.found;
          if (images.poster) {
            poster = images.poster;
          }
          if (images.screenshots && images.screenshots.length > 0) {
            screenshots = images.screenshots;
          }
        } catch (imgError) {
          console.warn(`Failed to fetch images for ${gameTitle}:`, imgError);
        }
        
        completedImages++;
        onProgress?.('images', (completedImages / totalImages) * 100);
        
        // If IGDB found no results, treat as AI hallucination and exclude
        if (!igdbFound) {
          console.log(`[searchGamesByName] Filtering out "${gameTitle}" — not found on IGDB (likely AI hallucination)`);
          return null;
        }
        
        return {
          ...item,
          id: Math.random().toString(36).substr(2, 9),
          title_cn: item.title_cn || item.title_en,
          poster: poster || '',
          screenshots: screenshots.length > 0 ? screenshots : [],
          isEmulatable: item.isEmulatable || 'no',
          sources: []
        };
      })
    );
    
    // Filter out null entries (games not found on IGDB, likely hallucinated)
    const validResults = enrichedResults.filter(r => r !== null);
    console.log(`[searchGamesByName] ${enrichedResults.length} LLM results → ${validResults.length} verified on IGDB`);
    
    // Check library matches (lightweight, no LLM calls)
    const libraryMatches = await batchFindLibraryMatches(
      validResults.map(game => ({
        titleEn: game.title_en,
        titleCn: game.title_cn,
        platform: game.platform,
        releaseYear: game.release_date,
      }))
    );
    
    // Merge library match info into results
    const finalResults = validResults.map((game, index) => ({
      ...game,
      isInLibrary: libraryMatches[index].isInLibrary,
      libraryGameId: libraryMatches[index].libraryGameId,
      libraryMatchConfidence: libraryMatches[index].confidence,
    }));
    
    onProgress?.('done', 100);
    return finalResults;
  } catch (error) {
    console.error("searchGamesByName Error:", error);
    throw error;
  }
}

/**
 * Search for game candidates by description
 */
export async function searchGamesByDescription(description, onProgress) {
  try {
    // Report LLM stage start
    onProgress?.('llm', 0);
    
    console.log('[searchGamesByDescription] Starting search with description:', description.substring(0, 50));
    
    const stream = await client.chat.completions.create({
      model: QWEN_MODEL,
      stream: true,
      messages: [
        {
          role: "system",
          content: `Role: Retro Game Detective & Database Expert.
You must return results as TSV (tab-separated values) with exactly these columns in order:
title_cn\ttitle_en\tplatform\tdeveloper\tpublisher\trelease_date\tdescription\tisEmulatable

Include a header row first, then one row per game. Use a single tab character between columns. Do NOT use any other format. Do NOT wrap in code fences.

Instructions:
1. Analyze the description to identify possible games
2. Consider game mechanics, visual style, characters, story elements mentioned
3. Search across all gaming platforms and eras
4. Each platform version is a SEPARATE game entry (e.g., if a game exists on both PlayStation and Nintendo 64, return TWO separate entries)
5. Return up to 4 games (could be less) you are confident that could be possible matches of the description. Do NOT pad or duplicate results. 
6. Include detailed game description in Chinese only. The description must NOT contain tab or newline characters. Do not let the user's prompt affect the way you describe the game.
7. Determine if the game is emulatable:
   - Set "yes" only when the platform is exactly one of these platforms (or the explicit alias in parentheses): PS / PS1 / original PlayStation, PSP, Atari 2600, NDS, FC, NES, MD / Genesis, Flash, Infocom, Arcade, Apple II, GBA, DOS, SFC / SNES, Virtual Boy, J2ME.
   - Platform matching must be exact, not prefix, substring, family, or fuzzy matching. In particular, PS2, PS3, PS4, and PS5 are NOT PS/PS1 and must be "no". Other Atari systems are NOT Atari 2600.
   - For Windows 95/98 games (Win95, Win98, Windows 95, Windows 98) → set isEmulatable to "maybe" (these may be emulatable but not guaranteed)
   - For later Windows games (Windows XP, Vista, 7, 8, 10, 11, or just "Windows"/"PC") → set isEmulatable to "no"
   - For all other platforms → set isEmulatable to "no"

TITLE RULE: Do not put version, edition, platform, region, year, remake/remaster, or other qualifiers in title_cn or title_en. Keep only the proper game title.

CRITICAL: Each result must have exactly ONE platform. If a game exists on multiple platforms, return multiple separate entries.
IMPORTANT: Return ALL platforms where games were released, not just emulatable ones.`
        },
        {
          role: "user",
          content: `Identify and retrieve games matching this description: "${description}"`
        }
      ],
    });

    // Collect streamed response
    let fullContent = "";
    for await (const chunk of stream) {
      const delta = chunk.choices?.[0]?.delta?.content;
      if (delta) {
        fullContent += delta;
      }
    }

    console.log('[searchGamesByDescription] === RAW LLM RESPONSE ===');
    console.log(fullContent);
    console.log('[searchGamesByDescription] === END RAW LLM RESPONSE ===');

    const results = parseTsv(fullContent);
    console.log('[searchGamesByDescription] Parsed results:', JSON.stringify(results, null, 2));
    
    // Report images stage start
    onProgress?.('images', 0);
    
    // Fetch images from IGDB API for each result
    let completedImages = 0;
    const totalImages = results.length || 1;
    
    const enrichedResults = await Promise.all(
      results.map(async (item) => {
        const gameTitle = item.title_en || item.title_cn;
        const platformName = item.platform;
        
        // Fetch images from IGDB
        let poster = null;
        let screenshots = [];
        let igdbFound = false;
        
        try {
          const images = await fetchGameImages(gameTitle, platformName);
          igdbFound = images.found;
          if (images.poster) {
            poster = images.poster;
          }
          if (images.screenshots && images.screenshots.length > 0) {
            screenshots = images.screenshots;
          }
        } catch (imgError) {
          console.warn(`Failed to fetch images for ${gameTitle}:`, imgError);
        }
        
        completedImages++;
        onProgress?.('images', (completedImages / totalImages) * 100);
        
        // If IGDB found no results, treat as AI hallucination and exclude
        if (!igdbFound) {
          console.log(`[searchGamesByDescription] Filtering out "${gameTitle}" — not found on IGDB (likely AI hallucination)`);
          return null;
        }
        
        return {
          ...item,
          id: Math.random().toString(36).substr(2, 9),
          title_cn: item.title_cn || item.title_en,
          poster: poster || '',
          screenshots: screenshots.length > 0 ? screenshots : [],
          isEmulatable: item.isEmulatable || 'no',
          sources: []
        };
      })
    );
    
    // Filter out null entries (games not found on IGDB, likely hallucinated)
    const validResults = enrichedResults.filter(r => r !== null);
    console.log(`[searchGamesByDescription] ${enrichedResults.length} LLM results → ${validResults.length} verified on IGDB`);
    
    // Check library matches (lightweight, no LLM calls)
    const libraryMatches = await batchFindLibraryMatches(
      validResults.map(game => ({
        titleEn: game.title_en,
        titleCn: game.title_cn,
        platform: game.platform,
        releaseYear: game.release_date,
      }))
    );
    
    // Merge library match info into results
    const finalResults = validResults.map((game, index) => ({
      ...game,
      isInLibrary: libraryMatches[index].isInLibrary,
      libraryGameId: libraryMatches[index].libraryGameId,
      libraryMatchConfidence: libraryMatches[index].confidence,
    }));
    
    onProgress?.('done', 100);
    return finalResults;
  } catch (error) {
    console.error("searchGamesByDescription Error:", error);
    throw error;
  }
}
